import { useEffect, useMemo, useState } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { credits } from '../../api/fee'
import type { CreditTransaction } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatCredit, formatDateTime } from '../../lib/format'
import { useEmailParam, useQueryParam } from '../../lib/useEmailParam'
import { useMoneyAction } from '../../lib/useMoneyAction'
import { AdminUserLocalMenu } from './AdminUserLocalMenu'

const NEW_CURRENCY = '+'
/** Legacy `admin-user-credit-view` cause select: `admin_credit` only when
 * adding, `admin_correction` in both modes. */
const CAUSES = { add: ['admin_credit', 'admin_correction'], remove: ['admin_correction'] } as const
/** A positive decimal, as typed — validated as a STRING (never parsed to a
 * float and re-serialized; RULES rule 3). */
const POSITIVE_AMOUNT = /^(?:\d+(?:\.\d+)?|\.\d+)$/

/**
 * Port of `AdminUserCreditViewComponent` (`…/admin/users/credit?email=`,
 * optional `&currency=`). BUSINESS-CRITICAL: add/remove credit mutates the
 * internal ledger directly (`POST …/admin/credit/add|remove`). Guards: the
 * synchronous double-submit guard of `useMoneyAction`, amount validated as a
 * positive decimal string and sent verbatim, and the ledger reloaded from the
 * server after every change (never updated optimistically).
 */
export function AdminUserCreditPage() {
  const { projectId, project } = useProject()
  const email = useEmailParam()
  const initialCurrency = useQueryParam('currency')
  const q = useApiQuery(() => (email ? credits.userCredits(projectId, email) : Promise.resolve([])), [projectId, email])
  const [selected, setSelected] = useState(initialCurrency)
  const [mode, setMode] = useState<'add' | 'remove' | null>(null)
  const [newCurrency, setNewCurrency] = useState('')
  const [amount, setAmount] = useState('')
  const [cause, setCause] = useState<string>('admin_credit')
  const [reference, setReference] = useState('')
  const actions = useMoneyAction()

  const infos = useMemo(() => q.data ?? [], [q.data])
  useEffect(() => {
    if (!selected && infos.length > 0) setSelected(infos[0]!.currency)
  }, [infos, selected])
  const current = infos.find((c) => c.currency === selected)

  const openForm = (m: 'add' | 'remove') => {
    setMode(m)
    setCause(CAUSES[m][0])
    setAmount('')
    setReference('')
    actions.setFeedback('credit', undefined)
  }

  const submit = async () => {
    if (!mode) return
    const trimmed = amount.trim()
    if (!POSITIVE_AMOUNT.test(trimmed) || Number(trimmed) <= 0) {
      actions.setFeedback('credit', { tone: 'warning', text: 'Enter a positive amount!' })
      return
    }
    // Legacy: a NEW currency typed on the "+" tab is upper-cased unless it
    // is exactly "credit".
    let currency = selected === NEW_CURRENCY ? newCurrency.trim() : selected
    if (!currency) {
      actions.setFeedback('credit', { tone: 'warning', text: 'Enter a currency.' })
      return
    }
    if (currency !== 'credit') currency = currency.toUpperCase()
    const body = { email, currency, amount: trimmed, cause, reference }
    const result = await actions.run('credit', JSON.stringify({ mode, ...body }), () =>
      mode === 'add' ? credits.add(projectId, body) : credits.remove(projectId, body),
    )
    if (result) {
      actions.setFeedback('credit', {
        tone: 'success',
        text: `${mode === 'add' ? 'Added' : 'Removed'} ${trimmed} ${currency}. New balance: ${formatCredit(result.balanceAfter, currency)}.`,
      })
      setMode(null)
      setSelected(currency)
      q.refetch()
    }
  }

  const columns: Column<CreditTransaction & { idx: number }>[] = [
    { key: 'idx', label: '#', align: 'right', numeric: true },
    { key: 'createdAt', label: 'Date', format: (v) => formatDateTime(v as string) },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      numeric: true,
      format: (v, row) => `${formatCredit(v as number, row.currency)} ${row.currency}`,
    },
    { key: 'result', label: 'Result', format: (v) => (Number(v) === 0 ? 'SUCCESS' : `Error code #${String(v)}`) },
    {
      key: 'balanceAfter',
      label: 'Balance after transaction',
      align: 'right',
      numeric: true,
      format: (v, row) => formatCredit(v as number, row.currency),
    },
    { key: 'cause', label: 'Cause' },
    { key: 'reference', label: 'Reference' },
  ]
  const txRows = (current?.transactions ?? []).map((t, i) => ({ ...t, idx: i + 1 }))
  const busy = actions.isBusy('credit')

  return (
    <div className="portal-card">
      <PageTitle title={`Credit info — ${project.name}`}>
        <AdminUserLocalMenu email={email} title="Credit info" />
      </PageTitle>
      {!email && <p className="portal-text-muted">Pick a user from the menu above.</p>}
      <QueryState loading={q.loading} error={q.error}>
        <nav className="portal-tabs" aria-label="Currencies">
          {[...infos.map((c) => c.currency), NEW_CURRENCY].map((c) => (
            <button
              key={c}
              type="button"
              className={selected === c ? 'portal-tab portal-tab--active' : 'portal-tab'}
              aria-pressed={selected === c}
              onClick={() => setSelected(c)}
            >
              {c === NEW_CURRENCY ? '+ new currency' : c}
            </button>
          ))}
        </nav>

        {current && (
          <dl className="portal-kv">
            <dt>Balance</dt>
            <dd className="fs-num">
              {formatCredit(current.balance, current.currency)} {current.currency}
            </dd>
            <dt>Last modified at</dt>
            <dd>{formatDateTime(current.lastModAt)}</dd>
            <dt>Created at</dt>
            <dd>{formatDateTime(current.createdAt)}</dd>
          </dl>
        )}

        {email && (
          <div className="portal-inline-actions">
            <button type="button" onClick={() => openForm('add')} disabled={busy}>
              + Add credit
            </button>
            <button type="button" onClick={() => openForm('remove')} disabled={busy || selected === NEW_CURRENCY}>
              − Remove credit
            </button>
          </div>
        )}

        {mode && (
          <form
            className="portal-form"
            aria-label={mode === 'add' ? 'Add credit' : 'Remove credit'}
            onSubmit={(e) => {
              e.preventDefault()
              void submit()
            }}
          >
            {selected === NEW_CURRENCY && (
              <label>
                Currency
                <input value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} placeholder="e.g. USDC" />
              </label>
            )}
            <label>
              Amount
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </label>
            <label>
              Cause
              <select value={cause} onChange={(e) => setCause(e.target.value)}>
                {CAUSES[mode].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Reference
              <input value={reference} onChange={(e) => setReference(e.target.value)} />
            </label>
            <div className="portal-inline-actions">
              <button type="submit" disabled={busy}>
                {busy ? 'Saving…' : mode === 'add' ? 'Add' : 'Remove'}
              </button>
              <button type="button" onClick={() => setMode(null)} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        )}
        <Notice feedback={actions.feedbackFor('credit')} />

        {current && (
          <div className="portal-table-scroll">
            <Table columns={columns} rows={txRows} rowKey={(r) => r.id || r.idx} />
          </div>
        )}
      </QueryState>
    </div>
  )
}
